import { useBoardLayoutCacheState } from "@components/views/board/useBoardLayoutCacheState";
import {
    ORGANIZATION_USER_CACHE_KEYS,
    useOrganizationUserCache,
} from "@hooks/useOrganizationUserCache";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { useScope } from "@state/scopeContext.lib";
import {
    DEFAULT_TASK_BOARD_LAYOUT_CACHE,
    type TaskBoardLayoutCache,
    taskBoardLayoutCacheSchema,
} from "./taskBoardCollapse";

interface UseTaskBoardCacheArgs {
    /** The organization page stores per organization, the workspace page per workspace. */
    isCrossWorkspace: boolean;
}

/**
 * The layout and the per-view collapse state the user keeps on the current
 * Tasks page. Stored settings that no longer parse fall back to their
 * defaults.
 */
export const useTaskBoardCache = ({
    isCrossWorkspace,
}: UseTaskBoardCacheArgs) => {
    const [workspaceCache, setWorkspaceCache] =
        useWorkspaceUserCache<TaskBoardLayoutCache>(
            !isCrossWorkspace
                ? WORKSPACE_USER_CACHE_KEYS.TASKS_PAGE_BOARD_CACHE_KEY
                : undefined,
            taskBoardLayoutCacheSchema,
        );
    const [organizationCache, setOrganizationCache] =
        useOrganizationUserCache<TaskBoardLayoutCache>(
            isCrossWorkspace
                ? ORGANIZATION_USER_CACHE_KEYS.CLIENTS_TASKS_PAGE_BOARD_CACHE_KEY
                : undefined,
            taskBoardLayoutCacheSchema,
        );
    const { activeWorkspaceKey, activeOrganizationKey } = useScope();

    return useBoardLayoutCacheState(
        isCrossWorkspace
            ? {
                  storedCache: organizationCache,
                  setStoredCache: setOrganizationCache,
                  scopeKey: activeOrganizationKey,
                  defaultCache: DEFAULT_TASK_BOARD_LAYOUT_CACHE,
              }
            : {
                  storedCache: workspaceCache,
                  setStoredCache: setWorkspaceCache,
                  scopeKey: activeWorkspaceKey,
                  defaultCache: DEFAULT_TASK_BOARD_LAYOUT_CACHE,
              },
    );
};
